import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, ErrorMessage } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import visaImage from '../../../../assets/images/visa-card.png';
import discoverImage from '../../../../assets/images/discover-card.png';
import paypalImage from '../../../../assets/images/paypal-card.png';
import mastercardImage from '../../../../assets/images/mastercard-card.png';
import americanExpressImage from '../../../../assets/images/americanexpress-card.png';
import { CSSTransition } from 'react-transition-group';
import BasicButton from '../../../../components/BasicButton';
import BasicCheckbox from '../../../../components/BasicCheckbox';
import BasicInput from '../../../../components/BasicInput';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSelect from '../../../../components/BasicSelect';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import CheckboxOrRadioGroup from '../../../../components/CheckboxOrRadioGroup';
import DateSelect from '../../../../components/DateSelect';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import {CHECKOUT_METHOD} from '../../../../constants/checkout';
import countries from './countries';
import './styles.css';
import {CheckoutFormValidationSchema} from './validate';

const CheckoutForm = props => {
  const {
    values: propValues,
    options = {
      storedCreditCards: [],
    },
    actions,
  } = props;

  const initialValues = useMemo(
    () => ({
      pid: null,
      firstName: '',
      lastName: '',
      email: '',
      method:
        propValues && propValues.storedCreditCard
          ? CHECKOUT_METHOD.STORED_CREDIT_CARD
          : null,
      cardNumber: '',
      cardCode: '',
      saveCreditCard: true,
      street: '',
      city: '',
      state: '',
      country: { value: 'US', label: 'United States' },
      postcode: '',
      storedCreditCard: null,
      expirationDate: null,
      ...propValues,
    }),
    [propValues]
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const result = [];
    for (let year = currentYear; year < currentYear + 21; ++year) {
      result.push({
        value: year,
        label: year,
      });
    }
    return result;
  }, []);

  const handleChangeCard = (field, value, { setFieldValue }) => {
    setFieldValue(field, value);
    setFieldValue('method', CHECKOUT_METHOD.STORED_CREDIT_CARD);
    setFieldValue('cardNumber', '');
    setFieldValue('expirationDate', null);
    setFieldValue('cardVerificationNumber', '');
    setFieldValue('cardCode', '');
    setFieldValue('street', '');
    setFieldValue('city', '');
    setFieldValue('state', '');
    setFieldValue('country', null);
    setFieldValue('postcode', '');
  };

  const handleChangeMethod = (e, { handleChange, setFieldValue }) => {
    handleChange(e);
    setFieldValue('storedCreditCard', null);
  };

  const handleClickRemoveCard = ({ value }, { setFieldValue }) => {
    actions.handleRemoveCard(value, value => {
      setFieldValue('storedCreditCard', value);
    });
  };

  const handleSendForm = (values, bag) => {
    let preparedValues = cloneDeep(values);

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null || data === '') {
        delete preparedValues[field];
      } else if (field === 'expirationDate') {
        preparedValues['cardExpirationMonth'] = preparedValues[field].month;
        preparedValues['cardExpirationYear'] = preparedValues[field].year;
        delete preparedValues[field];
      } else if (field === 'saveCreditCard') {
        preparedValues[field] = preparedValues[field] ? 1 : 0;
      } else if (typeof data === 'object') {
        preparedValues[field] = data.value;
      }
    });

    if (preparedValues['storedCreditCards']) {
      delete preparedValues['saveCreditCard'];
      delete preparedValues['country'];
    } else if (preparedValues['method'] === CHECKOUT_METHOD.PAYPAL) {
      preparedValues = {
        method: preparedValues['method'],
        pid: preparedValues['pid'],
        email: preparedValues['email'],
        firstName: preparedValues['firstName'],
        lastName: preparedValues['lastName'],
        returnUrl: preparedValues['returnUrl'],
      };
    }

    actions.handleCheckout(preparedValues, bag);
  };

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={initialValues}
          validationSchema={CheckoutFormValidationSchema}
          onSubmit={handleSendForm}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content"
              style={{ maxWidth: '100%' }}
            >
              {isSubmitting && <LoadMask />}
              <div className="form__group">
                <BasicLabel
                  name="contactInfo"
                  required={false}
                  text="Contact Info"
                  styles={{
                    fontWeight: 'bold',
                    marginBottom: '0',
                  }}
                />
              </div>
              <div className="form__group" style={{ maxWidth: '550px' }}>
                <BasicInput
                  label="First Name:"
                  required={true}
                  name="firstName"
                  inputType="text"
                  content={values.firstName}
                  placeholder="First Name..."
                  controlFunc={handleChange}
                  isValid={
                    (!errors.firstName && !!touched.firstName) ||
                    !touched.firstName
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ textTransform: 'capitalize' }}
                />

                <ErrorMessage
                  name="firstName"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group" style={{ maxWidth: '550px' }}>
                <BasicInput
                  label="Last Name:"
                  required={true}
                  name="lastName"
                  inputType="text"
                  content={values.lastName}
                  placeholder="Last Name..."
                  controlFunc={handleChange}
                  isValid={
                    (!errors.lastName && touched.lastName) || !touched.lastName
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ textTransform: 'capitalize' }}
                />

                <ErrorMessage
                  name="lastName"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div
                className="form__group"
                style={{ marginBottom: '30px', maxWidth: '550px' }}
              >
                <BasicInput
                  label="Email:"
                  required={true}
                  name="email"
                  inputType="email"
                  content={values.email}
                  placeholder="Email..."
                  controlFunc={handleChange}
                  isValid={(!errors.email && !!touched.email) || !touched.email}
                  touchFunc={handleBlur}
                  labelStyles={{ textTransform: 'capitalize' }}
                />

                <ErrorMessage
                  name="email"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group" style={{ maxWidth: '550px' }}>
                <BasicLabel
                  name="paymentMethod"
                  required={false}
                  text="Payment Method"
                  styles={{
                    fontWeight: 'bold',
                    marginBottom: '0',
                  }}
                />
                <ErrorMessage
                  name="method"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              {options.storedCreditCards && !!options.storedCreditCards.length && (
                <>
                  <div className="form__group" style={{ maxWidth: '550px' }}>
                    <BasicLabel
                      name="storedCreditCard"
                      required={false}
                      text="My stored credit card:"
                      styles={{
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                      }}
                    />
                    <div
                      className="form__row"
                      style={{ alignItems: 'flex-end' }}
                    >
                      <div className="form__row-element">
                        <BasicSelect
                          isSearchable={false}
                          name="storedCreditCard"
                          placeholder="Select..."
                          value={values.storedCreditCard}
                          options={options.storedCreditCards}
                          isValid={
                            (!errors.storedCreditCard &&
                              !!touched.storedCreditCard) ||
                            !touched.storedCreditCard
                          }
                          onChange={(field, value) =>
                            handleChangeCard(field, value, { setFieldValue })
                          }
                          onBlur={setFieldTouched}
                          labelStyles={{ textTransform: 'capitalize' }}
                        />
                      </div>
                      <div className="form__row-element">
                        <BasicButton
                          btnText="Remove selected"
                          btnArrow={false}
                          disabled={!values.storedCreditCard}
                          controlFunc={() =>
                            handleClickRemoveCard(values.storedCreditCard, {
                              setFieldValue,
                            })
                          }
                        />
                      </div>
                    </div>
                    <ErrorMessage
                      name="storedCreditCard"
                      render={message => (
                        <div className="form__error">{message}</div>
                      )}
                    />
                  </div>

                  <div className="form__group" style={{ maxWidth: '550px' }}>
                    <BasicLabel
                      name="or"
                      required={false}
                      text="or"
                      styles={{
                        fontWeight: 'bold',
                      }}
                    />
                  </div>
                </>
              )}
              <div className="form__group">
                <div className="form__row">
                  <CheckboxOrRadioGroup
                    name="method"
                    type="radio"
                    optionsObjectList={[
                      {
                        value: CHECKOUT_METHOD.VISA,
                        image: visaImage,
                      },
                      {
                        value: CHECKOUT_METHOD.MASTERCARD,
                        image: mastercardImage,
                      },
                      {
                        value: CHECKOUT_METHOD.DISCOVER,
                        image: discoverImage,
                      },
                      {
                        value: CHECKOUT_METHOD.AMEX,
                        image: americanExpressImage,
                      },
                      {
                        value: CHECKOUT_METHOD.PAYPAL,
                        image: paypalImage,
                      },
                    ]}
                    selectedOptions={values.method}
                    directionRow={true}
                    checkboxStyles={{ top: '7px' }}
                    controlFunc={e =>
                      handleChangeMethod(e, { handleChange, setFieldValue })
                    }
                  />
                </div>
              </div>
              {values.method &&
                [
                  CHECKOUT_METHOD.PAYPAL,
                  CHECKOUT_METHOD.STORED_CREDIT_CARD,
                ].indexOf(values.method) === -1 && (
                  <div>
                    <div className="form__group" style={{ maxWidth: '550px' }}>
                      <BasicInput
                        label="Card Number (no dashes or spaces):"
                        required={true}
                        name="cardNumber"
                        content={values.cardNumber}
                        placeholder="Card Number..."
                        controlFunc={handleChange}
                        isValid={
                          (!errors.cardNumber && !!touched.cardNumber) ||
                          !touched.cardNumber
                        }
                        touchFunc={handleBlur}
                        labelStyles={{ textTransform: 'capitalize' }}
                      />

                      <ErrorMessage
                        name="cardNumber"
                        render={message => (
                          <div className="form__error">{message}</div>
                        )}
                      />
                    </div>

                    <div className="form__group" style={{ maxWidth: '550px' }}>
                      <DateSelect
                        required
                        label="Card Expiration Date:"
                        labelStyles={{
                          textTransform: 'capitalize',
                        }}
                        name="expirationDate"
                        controlFunc={setFieldValue}
                        touchFunc={setFieldTouched}
                        values={values.expirationDate}
                        yearOptions={yearOptions}
                        showMonthLikeNumber={true}
                        isValid={
                          !(
                            errors.expirationDate && !!touched.expirationDate
                          ) || !touched.expirationDate
                        }
                      />

                      {(errors.cardExpirationMonth ||
                        errors.cardExpirationYear ||
                        (errors.expirationDate &&
                          touched &&
                          touched.expirationDate)) && (
                        <div className="form__error">
                          {errors.cardExpirationMonth ||
                            errors.cardExpirationYear ||
                            errors.expirationDate}
                        </div>
                      )}
                    </div>

                    <div className="form__group" style={{ maxWidth: '550px' }}>
                      <BasicInput
                        label="Card Verification Code (3-4 digits):"
                        required={true}
                        name="cardCode"
                        content={values.cardCode}
                        placeholder=""
                        controlFunc={handleChange}
                        isValid={
                          (!errors.cardCode && !!touched.cardCode) ||
                          !touched.cardCode
                        }
                        touchFunc={handleBlur}
                        labelStyles={{ textTransform: 'capitalize' }}
                      />

                      <ErrorMessage
                        name="cardCode"
                        render={message => (
                          <div className="form__error">{message}</div>
                        )}
                      />
                    </div>

                    <div className="form__group" style={{ maxWidth: '550px' }}>
                      <BasicCheckbox
                        value={values.saveCreditCard}
                        singleRow
                        checked={values.saveCreditCard}
                        title="Securely save credit card for future payments"
                        name="saveCreditCard"
                        controlFunc={handleChange}
                        labelStyles={{
                          fontFamily: 'Noto Sans, sans-serif',
                        }}
                      />
                    </div>

                    <div
                      className="form__group"
                      style={{ maxWidth: '550px', marginTop: '15px' }}
                    >
                      <BasicLabel
                        name="billingAddress"
                        required={false}
                        text="Billing Address"
                        styles={{
                          fontWeight: 'bold',
                          marginBottom: '0',
                        }}
                      />
                    </div>

                    <div className="form__group" style={{ maxWidth: '550px' }}>
                      <BasicInput
                        label="Street Address:"
                        required={true}
                        name="street"
                        inputType="text"
                        content={values.street}
                        placeholder="Street Address"
                        controlFunc={handleChange}
                        isValid={
                          (!errors.street && !!touched.street) ||
                          !touched.street
                        }
                        touchFunc={handleBlur}
                        labelStyles={{ textTransform: 'capitalize' }}
                      />

                      <ErrorMessage
                        name="street"
                        render={message => (
                          <div className="form__error">{message}</div>
                        )}
                      />
                    </div>

                    <div className="form__group" style={{ maxWidth: '550px' }}>
                      <div className="form__row">
                        <div className="form__row-element">
                          <BasicInput
                            label="City / Town:"
                            required={true}
                            name="city"
                            inputType="text"
                            content={values.city}
                            placeholder=""
                            controlFunc={handleChange}
                            isValid={
                              (!errors.city && !!touched.city) || !touched.city
                            }
                            touchFunc={handleBlur}
                            labelStyles={{ textTransform: 'capitalize' }}
                          />
                          <ErrorMessage
                            name="city"
                            render={message => (
                              <div
                                className="form__error"
                                style={{ position: 'absolute' }}
                              >
                                {message}
                              </div>
                            )}
                          />
                        </div>
                        <div className="form__row-element">
                          <BasicInput
                            label="State / Province:"
                            required={true}
                            name="state"
                            inputType="text"
                            content={values.state}
                            placeholder=""
                            controlFunc={handleChange}
                            isValid={
                              (!errors.state && touched.state) || !touched.state
                            }
                            touchFunc={handleBlur}
                            labelStyles={{ textTransform: 'capitalize' }}
                          />
                          <ErrorMessage
                            name="state"
                            render={message => (
                              <div
                                className="form__error"
                                style={{ position: 'absolute' }}
                              >
                                {message}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      className="form__group"
                      style={{ maxWidth: '550px', marginBottom: '10px' }}
                    >
                      <div className="form__row">
                        <div className="form__row-element">
                          <BasicLabel
                            name="country"
                            required={true}
                            text="Country:"
                            styles={{
                              fontWeight: 'bold',
                              textTransform: 'capitalize',
                            }}
                          />
                          <BasicSelect
                            isSearchable={true}
                            name="country"
                            value={values.country}
                            isValid={
                              (!errors.country && !!touched.country) ||
                              !touched.country
                            }
                            placeholder="Start typing..."
                            options={countries}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                          />

                          <ErrorMessage
                            name="country"
                            render={message => (
                              <div
                                className="form__error"
                                style={{ position: 'absolute' }}
                              >
                                {message}
                              </div>
                            )}
                          />
                        </div>
                        <div className="form__row-element">
                          <BasicInput
                            label="Postal / Zip Code:"
                            required={true}
                            name="postcode"
                            inputType="text"
                            content={values.postcode}
                            placeholder=""
                            controlFunc={handleChange}
                            isValid={
                              (!errors.postcode && !!touched.postcode) ||
                              !touched.postcode
                            }
                            touchFunc={handleBlur}
                            labelStyles={{ textTransform: 'capitalize' }}
                          />

                          <ErrorMessage
                            name="postcode"
                            render={message => (
                              <div
                                className="form__error"
                                style={{ position: 'absolute' }}
                              >
                                {message}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              <div className="form__controls form__controls--padding">
                <BasicSubmitButton
                  disabled={isSubmitting}
                  controlFunc={handleSubmit}
                  btnWidth={200}
                  btnHeight={40}
                  btnFontWeight={500}
                  btnFontSize="0.8571rem"
                  btnArrow={true}
                  btnTextColor="#27282d"
                  btnText="Process"
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

CheckoutForm.propTypes = {
  title: PropTypes.string,
  values: PropTypes.object,
};

export default CheckoutForm;
